import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  alpha,
  useTheme,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Check as CheckIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';

interface PricingPlan {
  name: string;
  monthlyPrice: number;
  annualPrice: number;
  description: string;
  features: string[];
  highlighted?: boolean;
  badge?: string;
  ctaText: string;
  maxUsers?: string;
}

const Pricing: React.FC = () => {
  const theme = useTheme();
  const [annual, setAnnual] = useState(false);

  const plans: PricingPlan[] = [
    {
      name: 'Free',
      monthlyPrice: 0,
      annualPrice: 0,
      description: 'Perfect for small teams getting started',
      maxUsers: 'Up to 5 users',
      ctaText: 'Start Free',
      features: [
        'Up to 5 team members',
        '3 projects',
        'Basic Kanban boards',
        'Issue tracking',
        'Sprint planning',
        'Mobile app access',
        'Community support',
        '5GB storage',
      ],
    },
    {
      name: 'Starter',
      monthlyPrice: 9,
      annualPrice: 90,
      description: 'For growing teams with advanced needs',
      maxUsers: 'Unlimited users',
      ctaText: 'Start Free Trial',
      features: [
        'Everything in Free',
        'Unlimited team members',
        'Unlimited projects',
        'Advanced analytics',
        'Time tracking',
        'Custom workflows',
        'Email support',
        '50GB storage',
        'Integrations (Jira, MS Planner)',
      ],
    },
    {
      name: 'Business',
      monthlyPrice: 19,
      annualPrice: 190,
      description: 'For teams that need AI power and insights',
      maxUsers: 'Unlimited users',
      highlighted: true,
      badge: 'Most Popular',
      ctaText: 'Start Free Trial',
      features: [
        'Everything in Starter',
        '🤖 AI-Powered Estimation',
        '📊 Leadership Dashboard',
        'Advanced reporting',
        'Custom fields',
        'API access',
        'Priority support',
        '200GB storage',
        'SSO (Single Sign-On)',
        'Advanced security',
      ],
    },
    {
      name: 'Enterprise',
      monthlyPrice: 49,
      annualPrice: 490,
      description: 'For organizations requiring custom solutions',
      maxUsers: 'Unlimited users',
      badge: 'Best Value',
      ctaText: 'Contact Sales',
      features: [
        'Everything in Business',
        'Dedicated account manager',
        'Custom integrations',
        'On-premise deployment',
        'SLA guarantee (99.9%)',
        '24/7 phone support',
        'Unlimited storage',
        'Custom training',
        'White-labeling',
        'Advanced compliance',
      ],
    },
  ];

  const getPrice = (plan: PricingPlan) => {
    return annual ? plan.annualPrice : plan.monthlyPrice;
  };

  const getSavings = (plan: PricingPlan) => {
    if (plan.monthlyPrice === 0) return 0;
    const monthlyTotal = plan.monthlyPrice * 12;
    const savings = monthlyTotal - plan.annualPrice;
    return Math.round((savings / monthlyTotal) * 100);
  };

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        bgcolor: theme.palette.background.default,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.03,
          backgroundImage: `radial-gradient(circle at 2px 2px, ${theme.palette.primary.main} 1px, transparent 0)`,
          backgroundSize: '40px 40px',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Stack spacing={2} alignItems="center" textAlign="center" sx={{ mb: 6 }}>
          {/* Section Badge */}
          <Typography
            variant="overline"
            sx={{
              color: theme.palette.secondary.main,
              fontWeight: 700,
              letterSpacing: 2,
              fontSize: '0.875rem',
            }}
          >
            PRICING
          </Typography>

          {/* Section Title */}
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              fontWeight: 800,
              maxWidth: '700px',
              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Simple, Transparent Pricing
          </Typography>

          {/* Section Subtitle */}
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.secondary,
              maxWidth: '600px',
              fontWeight: 400,
              lineHeight: 1.7,
            }}
          >
            Choose the perfect plan for your team. All plans include a 14-day free trial.
          </Typography>

          {/* Billing Toggle */}
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{
              mt: 3,
              p: 1,
              borderRadius: 3,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: annual ? 400 : 700,
                color: annual ? theme.palette.text.secondary : theme.palette.primary.main,
              }}
            >
              Monthly
            </Typography>
            <Switch
              checked={annual}
              onChange={(e) => setAnnual(e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: theme.palette.primary.main,
                },
              }}
            />
            <Typography
              variant="body1"
              sx={{
                fontWeight: annual ? 700 : 400,
                color: annual ? theme.palette.primary.main : theme.palette.text.secondary,
              }}
            >
              Annual
            </Typography>
            <Chip
              label="Save up to 17%"
              size="small"
              sx={{
                bgcolor: theme.palette.success.main,
                color: 'white',
                fontWeight: 700,
                fontSize: '0.75rem',
              }}
            />
          </Stack>
        </Stack>

        {/* Pricing Cards */}
        <Grid container spacing={3} sx={{ mt: 4 }}>
          {plans.map((plan, index) => (
            <Grid item xs={12} sm={6} lg={3} key={index}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  mt: plan.badge ? 2 : 0, // Add top margin when badge exists
                  border: plan.highlighted
                    ? `2px solid ${theme.palette.primary.main}`
                    : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  boxShadow: plan.highlighted
                    ? `0 12px 48px ${alpha(theme.palette.primary.main, 0.2)}`
                    : `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                  transition: 'all 0.3s ease',
                  transform: plan.highlighted ? 'scale(1.05)' : 'scale(1)',
                  '&:hover': {
                    transform: plan.highlighted ? 'scale(1.05)' : 'translateY(-8px)',
                    boxShadow: `0 16px 56px ${alpha(theme.palette.primary.main, 0.2)}`,
                  },
                }}
              >
                {/* Badge */}
                {plan.badge && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -16,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      bgcolor: plan.highlighted
                        ? theme.palette.primary.main
                        : theme.palette.secondary.main,
                      color: 'white',
                      px: 2,
                      py: 0.5,
                      borderRadius: 2,
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      whiteSpace: 'nowrap', // Prevent text wrapping
                      boxShadow: `0 4px 12px ${alpha(
                        plan.highlighted ? theme.palette.primary.main : theme.palette.secondary.main,
                        0.4
                      )}`,
                    }}
                  >
                    {plan.highlighted && <StarIcon sx={{ fontSize: 14 }} />}
                    {plan.badge}
                  </Box>
                )}

                <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  {/* Plan Name */}
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      mb: 1,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {plan.name}
                  </Typography>

                  {/* Description */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      mb: 3,
                      minHeight: '40px',
                    }}
                  >
                    {plan.description}
                  </Typography>

                  {/* Price */}
                  <Box sx={{ mb: 2 }}>
                    <Stack direction="row" alignItems="baseline" spacing={0.5}>
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 900,
                          color: theme.palette.primary.main,
                          fontSize: '2.5rem',
                        }}
                      >
                        ${getPrice(plan)}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        /{annual ? 'year' : 'month'}
                      </Typography>
                    </Stack>
                    {annual && plan.monthlyPrice > 0 && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.success.main,
                          fontWeight: 600,
                          display: 'block',
                          mt: 0.5,
                        }}
                      >
                        Save {getSavings(plan)}% with annual billing
                      </Typography>
                    )}
                    {plan.maxUsers && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.text.secondary,
                          display: 'block',
                          mt: 0.5,
                        }}
                      >
                        {plan.maxUsers}
                      </Typography>
                    )}
                  </Box>

                  {/* CTA Button */}
                  <Button
                    variant={plan.highlighted ? 'contained' : 'outlined'}
                    size="large"
                    fullWidth
                    sx={{
                      mb: 3,
                      py: 1.5,
                      fontWeight: 700,
                      ...(plan.highlighted && {
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        '&:hover': {
                          background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                        },
                      }),
                    }}
                  >
                    {plan.ctaText}
                  </Button>

                  {/* Features List */}
                  <List dense sx={{ flexGrow: 1 }}>
                    {plan.features.map((feature, featureIndex) => (
                      <ListItem key={featureIndex} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckIcon
                            sx={{
                              fontSize: 20,
                              color: theme.palette.success.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{
                            variant: 'body2',
                            color: 'text.secondary',
                            sx: { fontSize: '0.875rem' },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Bottom Info */}
        <Box
          sx={{
            mt: 8,
            p: 4,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.05
            )} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            textAlign: 'center',
          }}
        >
          <Stack spacing={2} alignItems="center">
            <TrendingUpIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />
            <Typography variant="h5" fontWeight={700}>
              All plans include a 14-day free trial
            </Typography>
            <Typography variant="body1" color="text.secondary" maxWidth="600px">
              No credit card required. Try all features risk-free. Cancel anytime with no
              questions asked.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                ✓ Cancel anytime
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ✓ No setup fees
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ✓ 24/7 support
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ✓ Secure payments
              </Typography>
            </Stack>
          </Stack>
        </Box>

        {/* FAQ Link */}
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography variant="body1" color="text.secondary">
            Have questions?{' '}
            <Typography
              component="span"
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 700,
                cursor: 'pointer',
                borderBottom: `2px solid ${theme.palette.primary.main}`,
                '&:hover': {
                  color: theme.palette.primary.dark,
                  borderColor: theme.palette.primary.dark,
                },
              }}
            >
              View FAQ
            </Typography>{' '}
            or{' '}
            <Typography
              component="span"
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 700,
                cursor: 'pointer',
                borderBottom: `2px solid ${theme.palette.primary.main}`,
                '&:hover': {
                  color: theme.palette.primary.dark,
                  borderColor: theme.palette.primary.dark,
                },
              }}
            >
              Contact Sales
            </Typography>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Pricing;

